import HackleCore from "../../core/HackleCore"
import Logger from "../../core/internal/logger"
import { HackleUserManualOverrideStorage } from "../../core/internal/evaluation/service/experiment/match/ManualOverrideStorage"
import { Metrics } from "../../core/internal/metrics/Metrics"
import { HackleUserExplorerBase, HackleUserExplorerImpl } from "../../core/internal/user/UserExplorer"
import { UserManager } from "../../core/internal/user/UserManager"
import { EvaluationMode } from "../../public/model"

const log = Logger.log

/**
 * The devtools bridge injected by the host page through `config.devTool`.
 */
export interface DevToolsManager {
  userExplorer(userExplorer: HackleUserExplorerBase): void

  close(): void
}

export interface DevToolsConfig {
  evaluationMode: EvaluationMode
  manager?: DevToolsManager
  core: HackleCore
  userManager: UserManager
  abOverrideStorage: HackleUserManualOverrideStorage
  ffOverrideStorage: HackleUserManualOverrideStorage
}

export class DevTools {
  constructor(
    private readonly manager: DevToolsManager,
    private readonly userExplorer: HackleUserExplorerBase
  ) {}

  showUserExplorer(): void {
    log.debug("UserExplorer opened.")
    Metrics.counter("user.explorer.show", {}).increment()
    this.manager.userExplorer(this.userExplorer)
  }

  hideUserExplorer(): void {
    this.manager.close()
  }
}

/**
 * Resolves the devtools bridge for the current configuration.
 *
 * Returns `null` when devtools must stay inactive:
 * - no valid manager was injected, or
 * - the client runs in remote evaluation mode, where the on-device workspace config that the user
 *   explorer requires does not exist. The warning is emitted only when a manager was actually
 *   injected, so pages without devtools stay quiet.
 */
export function resolveDevTools(config: DevToolsConfig): DevTools | null {
  const manager = config.manager
  if (!manager || typeof manager.userExplorer !== "function") {
    return null
  }

  if (config.evaluationMode === "remote") {
    log.warn("UserExplorer is not supported in remote evaluation mode.")
    return null
  }

  const userExplorer = new HackleUserExplorerImpl(
    config.core,
    config.userManager,
    config.abOverrideStorage,
    config.ffOverrideStorage
  )
  return new DevTools(manager, userExplorer)
}
